import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { FileText, Printer, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TEMPLATES } from "@/lib/suratTemplates";
import { FieldSelect } from "@/components/FieldSelect";

const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const COLORS = ["#0b4f4a", "#f59e0b", "#e11d48", "#0284c7", "#059669", "#7c3aed", "#b45309", "#64748b"];
const short = (id) => TEMPLATES.find((t) => t.id === id)?.judul.replace("Surat Keterangan ", "Ket. ").replace("Surat ", "") || id;

export const RekapSuratBulanan = () => {
  const [tahun, setTahun] = useState(String(new Date().getFullYear()));
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/stats/surat", { params: { tahun } }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [tahun]);

  const top = useMemo(() => (d?.per_jenis || []).slice(0, 7).map((j) => j.jenis), [d]);
  const chart = useMemo(() => (d?.bulan || []).map((b) => {
    const row = { name: BLN[b.bulan - 1] };
    let other = b.total;
    top.forEach((j) => { row[j] = b.per_jenis[j] || 0; other -= row[j]; });
    if (other > 0) row.Lainnya = other;
    return row;
  }), [d, top]);
  const years = Array.from(new Set([...(d?.tahun_tersedia || []), new Date().getFullYear()])).sort().map(String);
  const peak = d ? d.bulan.reduce((a, b) => (b.total > a.total ? b : a), d.bulan[0]) : null;

  return (
    <section className="mt-8" data-testid="rekap-surat-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-amber-700">Layanan Surat</p>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Rekap Surat Bulanan</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <FieldSelect value={tahun} onChange={setTahun} options={years.map((y) => ({ value: y, label: `Tahun ${y}` }))} testid="rekap-surat-tahun" className="w-36" />
          <Link to="/agenda" className="inline-flex items-center gap-2 text-sm border bg-white rounded-md px-3 h-10 hover:bg-slate-50" data-testid="rekap-surat-agenda-link"><BookOpen className="h-4 w-4" /> Buku Agenda</Link>
          <a href={`/cetak-rekap-surat?tahun=${tahun}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-white bg-[#0b4f4a] hover:bg-[#072f2c] rounded-md px-3 h-10" data-testid="rekap-surat-print-button"><Printer className="h-4 w-4" /> Cetak Rekap Surat</a>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Mini label={`Total surat ${tahun}`} value={d?.total || 0} testid="rekap-surat-total" />
        <Mini label="Jenis terbanyak" value={d?.per_jenis?.[0] ? short(d.per_jenis[0].jenis) : "-"} sub={d?.per_jenis?.[0] ? `${d.per_jenis[0].total} surat` : ""} testid="rekap-surat-top" />
        <Mini label="Bulan tersibuk" value={peak && peak.total ? BLN[peak.bulan - 1] : "-"} sub={peak && peak.total ? `${peak.total} surat` : ""} testid="rekap-surat-peak" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border rounded-2xl p-5 lg:col-span-2" data-testid="chart-surat-bulanan">
          <h3 className="font-semibold text-slate-900">Surat Terbit per Bulan menurut Jenis</h3>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e2d8" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip formatter={(v, n) => [v, short(n)]} />
                <Legend formatter={(n) => short(n)} wrapperStyle={{ fontSize: 11 }} />
                {top.map((j, i) => <Bar key={j} dataKey={j} stackId="s" fill={COLORS[i]} />)}
                {chart.some((r) => r.Lainnya) && <Bar dataKey="Lainnya" stackId="s" fill={COLORS[7]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border rounded-2xl p-5" data-testid="list-surat-jenis">
          <h3 className="font-semibold text-slate-900 mb-3">Per Jenis Surat</h3>
          {!d?.per_jenis?.length && <p className="text-sm text-slate-500">Belum ada surat di tahun ini.</p>}
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {(d?.per_jenis || []).map((j, i) => (
              <div key={j.jenis} className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-lg grid place-items-center text-white shrink-0" style={{ background: COLORS[Math.min(i, 7)] }}><FileText className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{short(j.jenis)}</div>
                  <div className="h-1.5 bg-slate-100 rounded mt-1"><div className="h-full rounded" style={{ width: `${(j.total / d.per_jenis[0].total) * 100}%`, background: COLORS[Math.min(i, 7)] }} /></div>
                </div>
                <b className="text-sm tabular-nums">{j.total}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white border rounded-2xl mt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="tabel-surat-bulanan">
          <thead className="bg-slate-50 text-slate-500 uppercase">
            <tr><th className="px-3 py-2.5 text-left">Jenis Surat</th>{BLN.map((b) => <th key={b} className="px-2 py-2.5 text-right">{b}</th>)}<th className="px-3 py-2.5 text-right">Total</th></tr>
          </thead>
          <tbody>
            {(d?.per_jenis || []).map((j) => (
              <tr key={j.jenis} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{short(j.jenis)}</td>
                {d.bulan.map((b) => <td key={b.bulan} className="px-2 py-2 text-right tabular-nums text-slate-600">{b.per_jenis[j.jenis] || "·"}</td>)}
                <td className="px-3 py-2 text-right font-bold">{j.total}</td>
              </tr>
            ))}
            <tr className="border-t bg-amber-50 font-bold">
              <td className="px-3 py-2">JUMLAH</td>
              {(d?.bulan || []).map((b) => <td key={b.bulan} className="px-2 py-2 text-right">{b.total}</td>)}
              <td className="px-3 py-2 text-right">{d?.total || 0}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

const Mini = ({ label, value, sub, testid }) => (
  <div className="bg-white border rounded-2xl p-4" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="font-display text-2xl font-bold text-slate-900 mt-1 truncate">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);
